import { Message } from "@aws-sdk/client-lex-runtime-v2";
import { XYPosition } from "react-flow-renderer";
import { Job } from "../../../../AdminPortal/scenes/Job/interface";

export enum InterviewStatusEnum {
  INACTIVE = "INACTIVE",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  EXPIRED = "EXPIRED"
}

export interface InterviewDetails {
  id: string
  status: string
  startDateTime: Date
  endDateTime: Date
  type: string
  intervieweeName: string
  intervieweeEmail: string
}

export enum CreateInterviewStatus {
  success = 'success',
  fail = 'fail',
  neutral = 'neutral',
  loading = 'loading'
}

export enum InterviewDetailsStatus {
  success = 'success',
  fail = 'fail',
  neutral = 'neutral',
  loading = 'loading'
}
/**
 * Used for archiving conversations and displaying message bubbles/boxes
 */
// export interface MessageDetails {
//   node_id?: number
//   branch_id?: number
//   message: Message
//   header: string
//   timestamp: string
//   score?: number
// }

export type Question = {
  id?: number

  /**
   * Type of question (Delegated, Open-ended, etc).
   */
  typeOfQuestion: string

  /**
   * Name of Intent, must match with what is provided to Lex.
   */
  name: string

  /**
    * To inform the application if the current question is the first question of the session.
    * This will guide the application to start the session from this question.
    */
  isFirstQuestion: boolean

  /**
    * To inform the application if the current question will be the last question of the session.
    * This will guide the application to end the session upon completing this question.
    */
  isLastQuestion: boolean

  /**
   * The String representation of the name of the slot type, whether it be an
   * Amazon Lex in-built slot type or custom slot type.
  */
  slotType: string

  /**
   * The messages to prompt a user for the slot values.
   */
  slotPrompt: string

  /**
   * The String value of the next intent name or dialog action to step into upon
   * successful capture of the slot value.
   */
  nextStep: string
}


/** 
 * Yes-no type of questions, which would require information on how the conversation would flow if yes or no.
 */
export type ConfirmationQuestion = Question & {
  typeOfQuestion: QuestionType.ConfirmationQuestion

  /**
   * The score to be given to the candidate for responding negatively.
   */
  scoreIfNo: number

  /**
   * The score to be given to the candidate for responding positively.
   */
  scoreIfYes: number

  /**
   * Additional logic to manipulate the next questions
   */
  confirmationSetting: ConfirmationSetting
}

/**
 * Questions where control will be delegated to Lex to capture the appropriate response.
 */
export type DelegatedQuestion = Question & {
  typeOfQuestion: QuestionType.DelegatedQuestion

  /**
   * Specify whether to accept a single or multiple values
   */
  multipleValues: boolean
}

/**
 * Questions that expects open ended response.
 * Will capture a response text as it is without any NLU.
 */
export type OpenEndedQuestion = Question & {
  typeOfQuestion: QuestionType.OpenEndedQuestion
}

/** 
 * Questions with conditional statements, which would require information on the type of condition, and how the conversation
 * would flow if a condition is fulfilled or not
 */
export type ConditionalQuestion = Question & {
  typeOfQuestion: "ConditionalQuestion"

  /**
   * Contains the ConfirmationSetting for the intent which will be used to build on Lex during runtime.
   */
  confirmationSetting?: ConfirmationSetting

  /**
   * Contains the branching information with its conditional expressions to determine which branch to step into in runtime.
   */
  conditionalSetting: ConditionalSetting
}

/**
 * The collection of Branch objects with each branch containing the condition
 * expression, response, and its corresponding step action.
 */
export interface Branch {
  id: number
  /**
   * The name of the branch, will be automatically assigned by default.
   */
  name: string

  /**
   * The String representation of the expression to check. NOTE: Lex replaces
   * {SlotName} variables on runtime with the value of the slot name. Expressions
   * should then be "{SlotName} >= 3".
   
   * Expressions include the following:
   * Comparison Operators: (=, !, >=, <=),
   * Boolean Operators: (AND (&&) OR(||) NOT (!)),
   * Quantifier Operators: ({SlotName} CONTAINS "value").
   * 
   * NOTE: This expression is currently linear (e.g.: ConditionA AND ConditionB OR ConditionC...)
   * Design will have to be amended and algorithm for building Expression will need to be changed to fit more complicated
   * algorithm building. (e.g.: (ConditionA AND ConditionB) OR ConditionC...)
   */
  expression: Expression

  /**
   * The String representing the next intent name or dialog action to be taken
   * upon qualifying for this branch.
   */
  nextStep: string

  /**
   * The score to be used for tabulating after the interview has been completed.
   */
  branchScore: number
}

export interface Expression {
  id: number

  /**
   * Contains the expression query to be built in runtime
   */
  queries: ExpressionQuery[]

  /**
   * Contains the condition to be applied to all expression queries.
   */
  condition?: ConditionEnum

  /**
   * The referencing branch.
   */
  branch: Branch
}

export interface ExpressionQuery {
  /**
   * The slotName being used in order to reference back to the value of the slot in runtime
   */
  slotName: string

  /**
   * The conditional operator being used for comparing.
   */
  conditionOperator: ConditionArithmeticOperatorEnum | ConditionQuantifierOperatorEnum

  /**
   * The expected value to form the condition
   */
  value: string
}

/**
 * The in-built arithmetic operators supported by Lex
 */
export enum ConditionArithmeticOperatorEnum {
  EQUALS = "=",
  GREATER_THAN = ">",
  LESSER_THAN = "<",
  NOT_EQUALS = "!=",
  GREATER_THAN_OR_EQUALS = ">=",
  LESSER_THAN_OR_EQUALS = "<="
}

/**
 * The in-built quantifier operator supported by Lex
 */
export enum ConditionQuantifierOperatorEnum {
  CONTAINS = "CONTAINS"
}

/**
 * The in-built conditional operator supported by Lex
 */
export enum ConditionEnum {
  AND = "AND",
  OR = "OR"
}


export enum QuestionType {
  ConfirmationQuestion = "ConfirmationQuestion",
  OpenEndedQuestion = "OpenEndedQuestion",
  ConditionalQuestion = "ConditionalQuestion",
  DelegatedQuestion = "DelegatedQuestion"
}

/**
 *The ConfirmationSetting for the intent which will be used to build on Lex during runtime.
 */
export interface ConfirmationSetting {
  /**
   * The name of the next intent to step into if candidate responds with a positive sentiment.
   */
  flowToIfYes: string

  /**
   * The name of the next intent to step into if candidate responds with a
   * negative sentiment.
   */
  flowToIfNo: string
}


/**
 * Contains the branching information with its conditional expressions to determine which branch to step into in runtime.
 */
export interface ConditionalSetting {

  /**
   * The response messages to be expressed when the condition is defaulted. e.g.,
   * none of the conditions were fulfilled.
   */
  defaultBranchResponse: string[]

  /**
   * The String representation of the next intent name or dialog action type to
   * step into when defaulting.
   */
  defaultStep: string

  /**
   * The collection of Branch objects with each branch containing the condition
   * expression, response, and its corresponding step action.
   */
  branches: Branch[]

  /**
   * The score to be tabulated at the end of the interview.
   */
  defaultBranchScore: number
}

/**
 * The in-built slot type provided by Lex.
 */
export enum AmazonLexInbuiltSlotType {
  Alphanumeric = "AlphaNumeric",
  Cities = "City",
  Countries = "Country",
  Date = "Date",
  Duration = "Duration",
  Email = "EmailAddress",
  FreeText = "FreeFormInput",
  Numerical = "Number",
  Percentage = "Percentage",
  PhoneNumber = "PhoneNumber",
  State = "State",
  StreetName = "StreetName",
  Time = "Time",
  FirstName = "FirstName",
  LastName = "LastName"
}

// export enum DialogActions {
//   EndConveration = "EndConversation",
//   ConfirmIntent = "ConfirmIntent",
//   EvaluateConditional = "EvaluateConditional",
//   CloseIntent = "CloseIntent"
// }

/**
 * Discriminated Union type, refer to TypeScript docs.
 * https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html
 * https://www.typescriptlang.org/docs/handbook/2/narrowing.html
 */
export type IntentDetails = ConfirmationQuestion | OpenEndedQuestion | ConditionalQuestion | DelegatedQuestion

export type InterviewNode = IntentDetails & {
  hashId?: number
  id?: number
  interviewFlowId?: number
  position: XYPosition
}